/**
 * Per-ship look state shared by the hull slot materials (A5) and the engine, light and shield effects
 * (A11). applyShipLook creates one per instance and stores it on every mesh of the instance as
 * `mesh.userData[LOOK_STATE_KEY]`, so shared per-(slot, faction) materials read per-ship values through
 * `uniform(...).onObjectUpdate(({ object }) => ...)` without one pipeline per ship (contract §8.2 note).
 * Asset-lane owned; fixed by the M1 architect so A5 and A11 can work concurrently.
 * Every field is written in place by the ShipLook setters: no allocation per frame (D-014).
 */
export const LOOK_STATE_KEY = 'eclLook'
export const MAX_HULL_HITS = 8
export const MAX_SHIELD_HITS = 4

export interface ShipLookState {
  /** Main and retro thrust output 0..1; boost 0 or 1. */
  thrust: number
  retro: number
  boost: number
  /** RCS drive, -1..1 in ship-local axes. */
  sway: number
  heave: number
  pitch: number
  yaw: number
  roll: number
  /** 0 = pristine, 1 = destroyed (1 - hull fraction). */
  damage: number
  /** Last MAX_HULL_HITS hull impacts as x, y, z (asset space, metres), t (library clock seconds). */
  readonly hullHits: Float32Array
  hullCursor: number
  /** Last MAX_SHIELD_HITS shield impacts, same layout. */
  readonly shieldHits: Float32Array
  shieldCursor: number
}

export function createLookState(): ShipLookState {
  return {
    thrust: 0,
    retro: 0,
    boost: 0,
    sway: 0,
    heave: 0,
    pitch: 0,
    yaw: 0,
    roll: 0,
    damage: 0,
    hullHits: new Float32Array(MAX_HULL_HITS * 4).fill(-1e6),
    hullCursor: 0,
    shieldHits: new Float32Array(MAX_SHIELD_HITS * 4).fill(-1e6),
    shieldCursor: 0,
  }
}

/** Writes an impact into a ring of (x, y, z, t) quads. Allocation-free. Returns the next cursor. */
export function recordHit(ring: Float32Array, cursor: number, x: number, y: number, z: number, t: number): number {
  const slots = ring.length / 4
  const i = (cursor % slots) * 4
  ring[i] = x
  ring[i + 1] = y
  ring[i + 2] = z
  ring[i + 3] = t
  return (cursor + 1) % slots
}
